import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { formatShoppingItem } from '../../utils/shoppingListHelpers';
import ShoppingListShareModal from './ShoppingListShareModal';
import { FaShareAlt, FaUsers } from 'react-icons/fa';
import '../../styles/recipes/QuickShoppingList.css';

const DEFAULT_LIST_STORAGE_KEY = 'quickShoppingListDefaultId';
const SECONDARY_LIST_STORAGE_KEY = 'quickShoppingListSecondaryId';

/**
 * Generiert einen Listennamen mit aktuellem Datum
 * Format: "Einkauf 30.01.2026"
 */
const generateListName = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `Einkauf ${day}.${month}.${year}`;
};

/**
 * Prüft ob eine Liste vollständig abgehakt ist (100% erledigt)
 */
const isListComplete = (list) => {
  if (!list || !list.items || list.items.length === 0) {
    return false; // Leere Liste gilt nicht als vollständig
  }
  const checkedItems = list.checkedItems instanceof Set
    ? list.checkedItems
    : new Set(list.checkedItems || []);
  return list.items.every((_, index) => checkedItems.has(index));
};

/**
 * Eine Liste gilt als Einkaufsliste nur, wenn sie explizit als 'SHOPPING'
 * getaggt ist - siehe TODO_ShoppingListType_Backend.md: ungetaggte
 * Bestandslisten (aus der Zeit vor dieser Unterscheidung) gelten bewusst
 * als To-Do, NICHT als Einkaufsliste (anders als man erwarten könnte).
 */
const isShoppingList = (list) => list.listType === 'SHOPPING';

/**
 * QuickShoppingList - Minimale Schnell-Einkaufszettel-Ansicht
 *
 * Öffnet automatisch einen Einkaufszettel mit Datum (z.B. "Einkauf 30.01.2026").
 * Falls alle Items abgehakt sind, wird automatisch eine neue Liste erstellt.
 */
const QuickShoppingList = ({ isOpen, onClose, sharedListId, onClearSharedList }) => {
  const { t } = useLanguage();
  const {
    shoppingLists,
    shoppingListsLoading,
    fetchShoppingLists,
    createManualShoppingList,
    deleteShoppingList,
    addItemToList,
    toggleItemChecked,
    removeItemFromList
  } = useRecipe();

  const [defaultListId, setDefaultListId] = useState(null);
  const [secondaryListId, setSecondaryListId] = useState(() => {
    // Initialize from localStorage immediately
    const stored = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
    return stored || null;
  });
  const [activeSlot, setActiveSlot] = useState(0); // 0 = primary (shared), 1 = secondary (private)
  const [newItemName, setNewItemName] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showListPicker, setShowListPicker] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Minimum swipe distance for gesture detection
  const minSwipeDistance = 50;

  // Determine which list to show: shared list takes priority, then slot-based selection
  const activeListId = sharedListId || (activeSlot === 0 ? defaultListId : secondaryListId);

  // Get secondary list from shoppingLists
  const secondaryList = secondaryListId
    ? shoppingLists.find(list => list.id === secondaryListId || String(list.id) === String(secondaryListId))
    : null;

  // Check if we have two lists configured
  const hasTwoLists = Boolean(defaultListId && secondaryListId);

  // Derive activeList from shoppingLists
  const activeList = activeListId
    ? shoppingLists.find(list => list.id === activeListId || String(list.id) === String(activeListId))
    : null;

  // Alle Einkaufslisten des Nutzers (für den "Andere Liste öffnen"-
  // Umschalter). Rein clientseitig aus den ohnehin schon geladenen Daten
  // gefiltert - kein zusätzlicher Request nötig.
  const allShoppingLists = shoppingLists
    .filter(isShoppingList)
    .filter(l => !String(l.id).startsWith('temp-'))
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

  // Frisch angelegte Listen bekommen zunächst eine temporäre "temp-..."-ID
  // (optimistic update) und werden erst asynchron mit der echten Backend-ID
  // synchronisiert (siehe RecipeContext.js). Sobald das passiert, aktualisiert
  // RecipeContext.js zwar schon localStorage - aber der eigene React-State
  // hier (defaultListId/secondaryListId) muss das noch nachziehen, sonst
  // "verschwindet" die gerade erst angelegte Liste aus der Ansicht.
  useEffect(() => {
    if (defaultListId && String(defaultListId).startsWith('temp-') &&
        !shoppingLists.some(l => String(l.id) === String(defaultListId))) {
      const stored = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY);
      if (stored && stored !== defaultListId) setDefaultListId(stored);
    }
    if (secondaryListId && String(secondaryListId).startsWith('temp-') &&
        !shoppingLists.some(l => String(l.id) === String(secondaryListId))) {
      const stored = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
      if (stored && stored !== secondaryListId) setSecondaryListId(stored);
    }
  }, [shoppingLists, defaultListId, secondaryListId]);

  // Check if we're viewing a shared list
  const isViewingSharedList = Boolean(sharedListId);
  const isSharedList = activeList?.isShared || (activeList?.isOwner === false);

  // Note: Shopping lists are loaded once on app start (RecipeContext)
  // Real-time updates come via WebSocket - no need to refetch on every tab switch

  // Validate secondaryListId when shopping lists are loaded. Eine gerade erst
  // angelegte "temp-..."-ID wird hier bewusst NICHT geprüft (nur der
  // Reconciliation-Effect oben behandelt sie) - sonst Race zwischen dem
  // Optimistic-Update in createManualShoppingList und dem anschließenden
  // localStorage.setItem()/setSecondaryListId() (siehe QuickTodoList.js für
  // Details, identisches Muster).
  useEffect(() => {
    if (shoppingListsLoading || !shoppingLists.length) return;

    const storedSecondaryId = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);
    if (!storedSecondaryId || storedSecondaryId.startsWith('temp-')) return;

    const secondaryExists = shoppingLists.find(
      list => list.id === storedSecondaryId || String(list.id) === storedSecondaryId
    );
    if (secondaryExists) {
      setSecondaryListId(storedSecondaryId);
    } else {
      // Secondary list no longer exists, clear it
      localStorage.removeItem(SECONDARY_LIST_STORAGE_KEY);
      setSecondaryListId(null);
    }
  }, [shoppingLists, shoppingListsLoading]);

  // Initialize: find or create default list (only if not viewing shared list)
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }

    // If viewing a shared list, skip default list initialization
    if (sharedListId) {
      setIsInitializing(false);
      return;
    }

    if (shoppingListsLoading || hasInitializedRef.current) {
      return;
    }

    const initializeDefaultList = async () => {
      setIsInitializing(true);
      setError(null);
      hasInitializedRef.current = true;

      try {
        // Get currently stored IDs
        const storedPrimaryId = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY);
        const storedSecondaryId = localStorage.getItem(SECONDARY_LIST_STORAGE_KEY);

        // Helper: Move old primary to secondary if not already set
        const moveOldPrimaryToSecondary = (newPrimaryId) => {
          if (storedPrimaryId &&
              String(storedPrimaryId) !== String(newPrimaryId) &&
              !storedSecondaryId) {
            // Check if old primary still exists and is not complete
            const oldPrimaryList = shoppingLists.find(
              list => (list.id === storedPrimaryId || String(list.id) === storedPrimaryId) && !isListComplete(list)
            );
            if (oldPrimaryList) {
              console.log('[QuickShoppingList] Alte Primary-Liste wird zur Secondary:', oldPrimaryList.name);
              localStorage.setItem(SECONDARY_LIST_STORAGE_KEY, String(storedPrimaryId));
              setSecondaryListId(storedPrimaryId);
            }
          }
        };

        // PRIORITY 1: Check for shared lists where user is a collaborator (not owner)
        // These should always take priority in "Heute Einkaufen"
        const sharedCollaboratorList = shoppingLists.find(
          list => isShoppingList(list) && list.isOwner === false && !isListComplete(list)
        );

        if (sharedCollaboratorList) {
          console.log('[QuickShoppingList] Geteilte Liste gefunden (als Collaborator):', sharedCollaboratorList.name);
          // Move old primary to secondary before setting new primary
          moveOldPrimaryToSecondary(sharedCollaboratorList.id);
          localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(sharedCollaboratorList.id));
          setDefaultListId(sharedCollaboratorList.id);
          setIsInitializing(false);
          return;
        }

        // PRIORITY 2: Check for shared lists that user owns and are actively shared
        const sharedOwnedList = shoppingLists.find(
          list => isShoppingList(list) && list.isOwner !== false &&
                  (list.isShared || (list.collaborators && list.collaborators.length > 0)) &&
                  !isListComplete(list)
        );

        if (sharedOwnedList) {
          console.log('[QuickShoppingList] Aktiv geteilte Liste gefunden (als Owner):', sharedOwnedList.name);
          // Move old primary to secondary before setting new primary
          moveOldPrimaryToSecondary(sharedOwnedList.id);
          localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(sharedOwnedList.id));
          setDefaultListId(sharedOwnedList.id);
          setIsInitializing(false);
          return;
        }

        // PRIORITY 3: Try to find by stored ID
        if (storedPrimaryId) {
          const existingList = shoppingLists.find(
            list => list.id === storedPrimaryId || String(list.id) === storedPrimaryId
          );

          if (existingList) {
            // Check if list is 100% complete - if so, create a new one
            if (isListComplete(existingList)) {
              console.log('[QuickShoppingList] Liste ist 100% abgehakt, erstelle neue Liste');
              // Don't delete the old list, just create a new one
              const newListName = generateListName();
              const newListId = await createManualShoppingList(newListName, '', [], { listType: 'SHOPPING' });
              localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(newListId));
              setDefaultListId(newListId);
              setIsInitializing(false);
              return;
            }

            // List exists and is not complete - use it (bewusst OHNE Typ-Filter:
            // eine über die gespeicherte ID direkt gefundene, bereits aktiv
            // genutzte Liste bleibt nutzbar, unabhängig vom listType)
            setDefaultListId(storedPrimaryId);
            setIsInitializing(false);
            return;
          }
        }

        // PRIORITY 4: Search for any incomplete manual list (fallback)
        const incompleteList = shoppingLists.find(
          list => list.isManual && isShoppingList(list) && !isListComplete(list)
        );

        if (incompleteList) {
          localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(incompleteList.id));
          setDefaultListId(incompleteList.id);
          setIsInitializing(false);
          return;
        }

        // No suitable list found - create one with date
        const newListName = generateListName();
        const newListId = await createManualShoppingList(newListName, '', [], { listType: 'SHOPPING' });
        localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(newListId));
        setDefaultListId(newListId);

      } catch (err) {
        console.error('Error finding/creating default shopping list:', err);
        setError(t('quickList.error') || 'Fehler beim Laden der Einkaufsliste');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeDefaultList();
  }, [isOpen, shoppingLists, shoppingListsLoading, sharedListId, createManualShoppingList, t]);

  // Handle exiting shared list view
  const handleExitSharedList = () => {
    onClearSharedList?.();
    // Re-initialize to show default list
    hasInitializedRef.current = false;
  };

  // Focus input when panel opens - kleiner Timeout, damit der Fokus nicht durch
  // die Öffnen-Animation/das Layout wieder verloren geht (v.a. auf Smartphones).
  useEffect(() => {
    if (isOpen && !isInitializing && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isInitializing]);

  // Handle adding a new item
  const handleAddItem = async () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName || !activeList || !activeList.id) {
      if (!activeList?.id) {
        console.error('Cannot add item: No active list or invalid list ID');
      }
      return;
    }

    // Check for local duplicate before sending to server
    const isDuplicate = activeList.items?.some(
      item => item.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setError(t('quickList.duplicateItem') || `"${trimmedName}" ist bereits in der Liste`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await addItemToList(activeList.id, {
        name: trimmedName,
        quantity: 0,
        unit: '',
        note: ''
      });
      setNewItemName('');
      setError(null);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error adding item:', err);
      const errorMsg = err?.response?.data?.message || err?.message || '';
      if (errorMsg.includes('existiert bereits') || errorMsg.includes('duplicate')) {
        setError(t('quickList.duplicateItem') || `"${trimmedName}" ist bereits in der Liste`);
      } else {
        setError(t('quickList.addError') || 'Fehler beim Hinzufügen');
      }
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Handle toggling item checked status
  const handleToggleItem = async (itemIndex) => {
    if (!activeList || !activeList.id) {
      console.error('Cannot toggle item: No active list or invalid list ID');
      return;
    }
    if (itemIndex === undefined || itemIndex === null || itemIndex < 0) {
      console.error('Cannot toggle item: Invalid item index', itemIndex);
      return;
    }
    try {
      await toggleItemChecked(activeList.id, itemIndex);
    } catch (err) {
      console.error('Error toggling item:', err);
    }
  };

  // Swipe handlers for switching between lists (Touch)
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    if (!hasTwoLists) return; // Only allow swipe if two lists are configured

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeSlot === 0) {
      // Swipe left: go to secondary (private) list
      setActiveSlot(1);
    } else if (isRightSwipe && activeSlot === 1) {
      // Swipe right: go to primary (shared) list
      setActiveSlot(0);
    }
  };

  // Mouse handlers for desktop swipe (drag)
  const onMouseDown = (e) => {
    // Ignore if clicking on interactive elements
    if (e.target.closest('button, input, a, .quick-list-item')) return;
    setIsDragging(true);
    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;
    if (!hasTwoLists) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && activeSlot === 0) {
      setActiveSlot(1);
    } else if (isRightSwipe && activeSlot === 1) {
      setActiveSlot(0);
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Handle refresh - fetch latest data from backend
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setShowOverflowMenu(false);
    try {
      await fetchShoppingLists();
    } catch (err) {
      console.error('Error refreshing shopping lists:', err);
      setError(t('quickList.error') || 'Fehler beim Aktualisieren');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Wechselt die im aktiven Slot angezeigte Liste auf eine andere,
   * ausgewählte Einkaufsliste (z.B. eine alte Bestandsliste, die sonst nicht
   * mehr erreichbar wäre, da es keine "Alle Listen"-Übersicht mehr gibt).
   */
  const handleSwitchToList = (listId) => {
    setShowListPicker(false);
    setShowOverflowMenu(false);
    if (activeSlot === 1) {
      localStorage.setItem(SECONDARY_LIST_STORAGE_KEY, String(listId));
      setSecondaryListId(listId);
    } else {
      localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, String(listId));
      setDefaultListId(listId);
    }
  };

  /**
   * Legt aus der Listen-Übersicht heraus eine neue leere Einkaufsliste an und
   * setzt sie direkt als die im aktiven Slot (Haupt- oder Zweitliste)
   * angezeigte Liste.
   */
  const handleCreateFromPicker = async () => {
    try {
      const newListId = await createManualShoppingList(generateListName(), '', [], { listType: 'SHOPPING' });
      handleSwitchToList(newListId);
    } catch (err) {
      console.error('Error creating shopping list:', err);
      setError(t('quickList.addError') || 'Fehler beim Anlegen der Liste');
      setTimeout(() => setError(null), 3000);
    }
  };

  /**
   * Löscht eine Einkaufsliste endgültig aus der Listen-Übersicht heraus.
   * Zeigt die gelöschte Liste gerade im Haupt- oder Zweit-Slot an, wird der
   * jeweilige Zeiger zurückgesetzt (die Init-Logik legt beim nächsten Öffnen
   * automatisch wieder eine frische Standardliste an).
   */
  const handleDeleteList = async (listId) => {
    if (!window.confirm(t('quickList.confirmDeleteList') || 'Diese Liste wirklich endgültig löschen?')) {
      return;
    }
    try {
      await deleteShoppingList(listId);
      if (String(defaultListId) === String(listId)) {
        localStorage.removeItem(DEFAULT_LIST_STORAGE_KEY);
        setDefaultListId(null);
        hasInitializedRef.current = false;
      }
      if (String(secondaryListId) === String(listId)) {
        localStorage.removeItem(SECONDARY_LIST_STORAGE_KEY);
        setSecondaryListId(null);
        setActiveSlot(0);
      }
    } catch (err) {
      console.error('Error deleting shopping list:', err);
      setError(t('quickList.deleteListError') || 'Fehler beim Löschen der Liste');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle removing an item
  const handleRemoveItem = async (itemIndex) => {
    if (!activeList || !activeList.id) {
      console.error('Cannot remove item: No active list or invalid list ID');
      return;
    }
    if (itemIndex === undefined || itemIndex === null || itemIndex < 0) {
      console.error('Cannot remove item: Invalid item index', itemIndex);
      return;
    }
    try {
      await removeItemFromList(activeList.id, itemIndex);
    } catch (err) {
      console.error('Error removing item:', err);
      setError(t('quickList.removeError') || 'Fehler beim Entfernen');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Calculate progress
  const totalItems = activeList?.items?.length || 0;
  const checkedCount = activeList?.checkedItems?.size || 0;
  const progressText = t('quickList.progress')
    ?.replace('{completed}', checkedCount)
    ?.replace('{total}', totalItems) || `${checkedCount} von ${totalItems} erledigt`;

  // Separate unchecked and checked items
  const uncheckedItems = activeList?.items?.filter((_, index) => {
    const isChecked = activeList.checkedItems?.has?.(index) ||
      (Array.isArray(activeList.checkedItems) && activeList.checkedItems.includes(index));
    return !isChecked;
  }) || [];

  const checkedItems = activeList?.items?.filter((_, index) => {
    const isChecked = activeList.checkedItems?.has?.(index) ||
      (Array.isArray(activeList.checkedItems) && activeList.checkedItems.includes(index));
    return isChecked;
  }) || [];

  // Get original indices for checked/unchecked items
  const getOriginalIndex = (item) => {
    return activeList?.items?.findIndex(i => i === item) ?? -1;
  };

  if (!isOpen) return null;

  return (
    <div className="quick-shopping-list-overlay" onClick={onClose}>
      <div
        className={`quick-shopping-list-panel${isDragging ? ' dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        {/* Add Item Input - Top Position (like search field) */}
        {!isInitializing && !shoppingListsLoading && activeList && (
          <div className="quick-list-add-section" data-testid="quick-list-add-section">
            <input
              ref={inputRef}
              type="text"
              className="quick-list-input"
              placeholder={t('quickList.placeholder') || 'Artikel hinzufügen...'}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="quick-list-input"
            />
            <button
              className="quick-list-add-btn"
              onClick={handleAddItem}
              disabled={!newItemName.trim()}
              data-testid="quick-list-add-btn"
            >
              +
            </button>
          </div>
        )}

        {/* Swipe Indicator - only show when two lists are configured */}
        {hasTwoLists && !isViewingSharedList && (
          <div className="quick-list-swipe-indicator">
            <button
              className={`swipe-dot ${activeSlot === 0 ? 'active' : ''}`}
              onClick={() => setActiveSlot(0)}
              title={t('quickList.sharedList') || 'Geteilte Liste'}
            />
            <button
              className={`swipe-dot ${activeSlot === 1 ? 'active' : ''}`}
              onClick={() => setActiveSlot(1)}
              title={t('quickList.privateList') || 'Private Liste'}
            />
          </div>
        )}

        {/* List Info Banner */}
        {!isViewingSharedList && activeList && (
          <div className={`quick-list-info-banner ${activeSlot === 1 ? 'private' : 'shared'}`}>
            {activeSlot === 0 ? (
              <>
                <span className="banner-icon">{activeList.isShared || (activeList.collaborators && activeList.collaborators.length > 0) ? '👥' : '🔗'}</span>
                <span className="banner-text">
                  {activeList.name}
                  {/* Show "Aktiv geteilt" if list is shared (has collaborators or isShared flag) */}
                  {(activeList.isShared || (activeList.collaborators && activeList.collaborators.length > 0)) ? (
                    <span className="banner-badge shared">{t('quickList.activelyShared') || '(Aktiv geteilt)'}</span>
                  ) : (
                    <span className="banner-badge">{t('quickList.primaryList') || '(Hauptliste)'}</span>
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="banner-icon">📝</span>
                <span className="banner-text">
                  {activeList.name}
                  <span className="banner-badge">{t('quickList.private') || '(Privat)'}</span>
                </span>
              </>
            )}
          </div>
        )}

        {/* Header - App Bar Style */}
        <div className="quick-list-header">
          <div className="quick-list-header-left">
            {isViewingSharedList && (
              <button className="quick-list-back-btn" onClick={handleExitSharedList}>
                ←
              </button>
            )}
            {/* Navigation buttons for switching between lists */}
            {hasTwoLists && (
              <div className="quick-list-nav-buttons">
                <button
                  className={`quick-list-nav-btn ${activeSlot === 0 ? 'disabled' : ''}`}
                  onClick={() => setActiveSlot(0)}
                  disabled={activeSlot === 0}
                  title={t('quickList.primaryList') || 'Hauptliste'}
                >
                  ‹
                </button>
                <button
                  className={`quick-list-nav-btn ${activeSlot === 1 ? 'disabled' : ''}`}
                  onClick={() => setActiveSlot(1)}
                  disabled={activeSlot === 1}
                  title={t('quickList.private') || 'Private Liste'}
                >
                  ›
                </button>
              </div>
            )}
          </div>
          <div className="header-center">
            {isViewingSharedList && isSharedList && (
              <h1 className="app-bar-title" data-testid="quick-list-title">
                <FaUsers className="shared-title-icon" />
                {' '}{t('share.sharedList') || 'Geteilte Liste'}
              </h1>
            )}
          </div>
          <div className="quick-list-header-actions">
            {activeList && !isViewingSharedList && (
              <button
                className="quick-list-share-btn"
                onClick={() => setShowShareModal(true)}
                title={t('share.shareList') || 'Liste teilen'}
              >
                <FaShareAlt />
              </button>
            )}
            <button
              className="quick-list-menu-btn"
              onClick={() => setShowOverflowMenu(!showOverflowMenu)}
              title={t('menu') || 'Menü'}
            >
              ⋮
            </button>
            {/* Overflow Dropdown Menu */}
            {showOverflowMenu && (
              <>
                <div className="overflow-backdrop" onClick={() => setShowOverflowMenu(false)} />
                <div className="overflow-dropdown">
                  <button
                    className="overflow-item"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? '⟳' : '↻'} {t('quickList.refresh') || 'Aktualisieren'}
                  </button>
                  <button
                    className="overflow-item"
                    onClick={() => {
                      setShowOverflowMenu(false);
                      setShowListPicker(true);
                    }}
                  >
                    📋 {t('quickList.switchList') || 'Listen verwalten'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Listen-Übersicht: alle Einkaufslisten des Nutzers - hier lässt sich
            eine bestehende Liste auswählen, eine neue anlegen oder eine
            bestehende endgültig löschen (siehe allShoppingLists oben) */}
        {showListPicker && (
          <>
            <div className="overflow-backdrop" onClick={() => setShowListPicker(false)} />
            <div className="quick-list-list-picker">
              <div className="list-picker-header">
                <h3>{t('quickList.switchListTitle') || 'Einkaufsliste wählen'}</h3>
                <button className="list-picker-close" onClick={() => setShowListPicker(false)}>✕</button>
              </div>
              {/* Ziel-Auswahl: legt fest, ob "Neue Liste anlegen" bzw. eine
                  ausgewählte Liste als Haupt- oder Zweitliste (Swipe-Slot)
                  verwendet wird - unabhängig davon, ob schon eine Zweitliste
                  existiert (sonst gäbe es keine Möglichkeit, überhaupt die
                  erste Zweitliste anzulegen). */}
              <div className="list-picker-slot-selector">
                <button
                  className={activeSlot === 0 ? 'active' : ''}
                  onClick={() => setActiveSlot(0)}
                >
                  {t('quickList.slotPrimary') || 'Hauptliste'}
                </button>
                <button
                  className={activeSlot === 1 ? 'active' : ''}
                  onClick={() => setActiveSlot(1)}
                >
                  {t('quickList.slotSecondary') || 'Zweite Liste'}
                </button>
              </div>
              <div className="list-picker-items">
                <button className="list-picker-item list-picker-add" onClick={handleCreateFromPicker}>
                  <span className="list-picker-item-name">➕ {t('quickList.newList') || 'Neue Liste anlegen'}</span>
                </button>
                {allShoppingLists.length === 0 ? (
                  <p className="list-picker-empty">{t('quickList.noOtherLists') || 'Keine weiteren Listen vorhanden'}</p>
                ) : (
                  allShoppingLists.map(list => {
                    const total = list.items?.length || 0;
                    const checked = list.checkedItems?.size ?? (Array.isArray(list.checkedItems) ? list.checkedItems.length : 0);
                    const isCurrent = String(list.id) === String(activeListId);
                    return (
                      <div key={list.id} className={`list-picker-row ${isCurrent ? 'current' : ''}`}>
                        <button
                          className="list-picker-item"
                          onClick={() => handleSwitchToList(list.id)}
                        >
                          <span className="list-picker-item-name">{list.name}</span>
                          <span className="list-picker-item-meta">
                            {isCurrent && '✓ '}{checked}/{total}
                          </span>
                        </button>
                        <button
                          className="list-picker-delete"
                          onClick={() => handleDeleteList(list.id)}
                          title={t('delete') || 'Löschen'}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}

        {/* Shared List Info Banner */}
        {isViewingSharedList && activeList && (
          <div className="shared-list-banner">
            <FaUsers />
            <span>{activeList.name}</span>
            {activeList.ownerName && (
              <span className="owner-info">
                ({t('share.sharedBy') || 'Geteilt von'} {activeList.ownerName})
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div className="quick-list-content">
          {/* Loading State */}
          {(isInitializing || shoppingListsLoading) && (
            <div className="quick-list-loading">
              <div className="loading-spinner"></div>
              <p>{t('loading') || 'Lädt...'}</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="quick-list-error">
              {error}
            </div>
          )}

          {/* Main Content */}
          {!isInitializing && !shoppingListsLoading && activeList && (
            <>
              {/* Items List */}
              <div className="quick-list-items" data-testid="quick-list-items">
                {activeList.items.length === 0 ? (
                  <div className="quick-list-empty">
                    <p>{t('quickList.empty') || 'Noch keine Artikel'}</p>
                    <p className="quick-list-empty-hint">
                      {t('quickList.emptyHint') || 'Füge deinen ersten Artikel hinzu!'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Unchecked Items */}
                    <ul className="quick-list-item-list">
                      {uncheckedItems.map((item) => {
                        const originalIndex = getOriginalIndex(item);
                        return (
                          <li
                            key={originalIndex}
                            className="quick-list-item"
                            data-testid="quick-list-item"
                          >
                            <button
                              className="quick-list-checkbox-btn"
                              onClick={() => handleToggleItem(originalIndex)}
                              aria-label="Mark as done"
                            >
                              <span className="checkbox-circle"></span>
                            </button>
                            <span className="quick-list-item-text">
                              {formatShoppingItem(item)}
                            </span>
                            <button
                              className="quick-list-item-delete"
                              onClick={() => handleRemoveItem(originalIndex)}
                              title={t('delete') || 'Löschen'}
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Progress Divider - between unchecked and checked */}
                    {totalItems > 0 && (
                      <div className="quick-list-progress-divider">
                        <span className="quick-list-progress-text">{progressText}</span>
                      </div>
                    )}

                    {/* Checked Items */}
                    {checkedItems.length > 0 && (
                      <ul className="quick-list-item-list checked-items">
                        {checkedItems.map((item) => {
                          const originalIndex = getOriginalIndex(item);
                          return (
                            <li
                              key={originalIndex}
                              className="quick-list-item checked"
                              data-testid="quick-list-item"
                            >
                              <button
                                className="quick-list-checkbox-btn checked"
                                onClick={() => handleToggleItem(originalIndex)}
                                aria-label="Unmark"
                              >
                                <span className="checkbox-circle">✓</span>
                              </button>
                              <span className="quick-list-item-text strikethrough">
                                {formatShoppingItem(item)}
                              </span>
                              <button
                                className="quick-list-item-delete"
                                onClick={() => handleRemoveItem(originalIndex)}
                                title={t('delete') || 'Löschen'}
                              >
                                ×
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Share Modal */}
        <ShoppingListShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          listId={activeList?.id}
          listName={activeList?.name}
        />
      </div>
    </div>
  );
};

export default QuickShoppingList;
